import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  Paper,
} from "@mui/material";

import { Visibility, VisibilityOff, Home as HomeIcon } from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import { useMessage } from "../contexts/MessageContext";
import { useAuth } from "../contexts/AuthContext";
import backgroundImage from "../assets/background.webp";
import companyLogo from "../assets/WWDS Logo white.png";

const Login = () => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const { showMessage } = useMessage();
  const { login: handleLogin } = useAuth();

  const handleInputChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setSuccess("");
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess("");

    try {
      const userData = await handleLogin(form.email, form.password);
      setSuccess("Login successful!");

      if (userData.role === "admin") {
        navigate("/admin");
      } else if (userData.role === "businessOwner") {
        navigate("/business");
      } else {
        showMessage("Unauthorized role. Contact support.");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Login failed. Please try again.";
      showMessage(errorMessage);
      setSuccess("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        position: "relative",
      }}
    >
      {/* Home Icon */}
      <Box
        sx={{
          position: "absolute",
          top: "1rem",
          left: "1rem",
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          cursor: "pointer",
        }}
        onClick={() => navigate("/")}
      >
        <IconButton>
          <HomeIcon fontSize="large" sx={{ color: "white" }} />
        </IconButton>
        
      </Box>
      <Paper
        elevation={6}
        sx={{
          width: { xs: "90%", sm: 400 },
          p: 4,
          borderRadius: 2,
          textAlign: "center",
          backgroundColor: "rgba(255, 255, 255, 0.95)",
        }}
      >
        <Typography
          variant="h4"
          component={Link}
          to="/"
          sx={{
            fontWeight: 700,
            color: "primary.main",
            textTransform: "uppercase",
            textDecoration: "none",
            mb: 3,
          }}
        >
          InsightHub
        </Typography>

        <Typography variant="h6" sx={{ mb: 2, color: "text.secondary" }}>
          Welcome Back! Please Login.
        </Typography>

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box component="form" onSubmit={onSubmit}>
          <TextField
            name="email"
            label="Email"
            type="email"
            variant="outlined"
            fullWidth
            margin="normal"
            value={form.email}
            onChange={handleInputChange}
          />

          <TextField
            name="password"
            label="Password"
            type={showPassword ? "text" : "password"}
            variant="outlined"
            fullWidth
            margin="normal"
            value={form.password}
            onChange={handleInputChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={togglePasswordVisibility} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{
              mt: 3,
              py: 1.5,
              fontWeight: "bold",
              textTransform: "uppercase",
            }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : "Login"}
          </Button>
        </Box>
      </Paper>

      {/* Footer */}
      <Box
        sx={{
          position: "absolute",
          bottom: "1rem",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          color: "rgba(255, 255, 255, 0.8)",
          width: "100%",
        }}
      >
        <img
          src={companyLogo}
          alt="WhiteWall Digital Solutions"
          style={{
            width: "120px",
            height: "auto",
            marginBottom: "0.5rem",
          }}
        />
        <Typography variant="body2" sx={{ mb: 1 }}>
          Need help?{" "}
          <a
            href="https://www.whitewall.om/#Contact-us"
            style={{
              textDecoration: "none",
              color: "#ffffff",
              fontWeight: "bold",
            }}
          >
            Contact us
          </a>
        </Typography>
        <Typography variant="body2">
          Powered by{" "}
          <a
            href="https://www.whitewall.om"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              textDecoration: "none",
              color: "#ffffff",
              fontWeight: "bold",
            }}
          >
            WhiteWall Digital Solutions
          </a>
        </Typography>
      </Box>
    </Box>
  );
};

export default Login;
